import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Archive, ChevronDown, RefreshCw, Wifi } from "lucide-react";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";

const colors = {
  bg: "#030303",
  panel: "#080604",
  header: "#009d8b",
  line: "rgba(255, 212, 93, 0.34)",
  red: "#ff253a",
  green: "#19ff6a",
  gold: "#f7c948",
  muted: "#a1a1aa",
};

function number(value) {
  return Number(value || 0);
}

function fmt(value, digits = 0) {
  return number(value).toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function rowColor(side) {
  if (side === "BUY") return colors.green;
  if (side === "SELL") return colors.red;
  return "#f8fafc";
}

function pressureLabel(row) {
  if (row.side === "SELL") return "SELL DUMP";
  if (row.side === "BUY") return "BUY LIFT";
  return row.reason || "NORMAL";
}

function displayQty(row, useSavedTotalFallback = false) {
  return useSavedTotalFallback && !number(row.volumeDelta)
    ? row.totalVolume
    : row.volumeDelta;
}

function displayValueCrores(row, useSavedTotalFallback = false) {
  return useSavedTotalFallback && !number(row.pressureValueCrores)
    ? row.tradedValueCrores
    : row.pressureValueCrores;
}

function signed(value, digits = 0) {
  const next = number(value);
  return `${next >= 0 ? "+" : ""}${fmt(next, digits)}`;
}

function timeToMinutes(value) {
  const [hour, minute] = String(value || "00:00").split(":").map(Number);
  return hour * 60 + minute;
}

function minuteLabel(totalMinutes) {
  return `${String(Math.floor(totalMinutes / 60)).padStart(2, "0")}:${String(totalMinutes % 60).padStart(2, "0")}`;
}

function stockSnapshotMinute(snapshot) {
  return String(snapshot?.minute || snapshot?.rows?.find(row => row.time)?.time || "").slice(0, 5);
}

function aggregateStockRows(records, fallbackRows = [], threshold = 3000) {
  if (!records?.length) return fallbackRows || [];

  const bySymbol = new Map();

  records.forEach(record => {
    (record.rows || []).forEach(row => {
      const current = bySymbol.get(row.symbol) || {
        ...row,
        volumeDelta: 0,
        pressureValueCrores: 0,
        firstLtp: row.ltp,
      };
      bySymbol.set(row.symbol, {
        ...current,
        ...row,
        volumeDelta: number(current.volumeDelta) + number(row.volumeDelta),
        pressureValueCrores: Number((number(current.pressureValueCrores) + number(row.pressureValueCrores)).toFixed(2)),
        firstLtp: current.firstLtp,
      });
    });
  });

  const rows = [...bySymbol.values()]
    .map(row => {
      const priceDelta = Number((number(row.ltp) - number(row.firstLtp)).toFixed(2));
      const side = row.volumeDelta >= threshold && priceDelta < 0
        ? "SELL"
        : row.volumeDelta >= threshold && priceDelta > 0
          ? "BUY"
          : "NEUTRAL";

      return {
        ...row,
        priceDelta,
        side,
        alert: row.volumeDelta >= threshold,
        reason: side === "SELL"
          ? "Sell quantity burst"
          : side === "BUY"
            ? "Buy quantity burst"
            : row.reason || "Normal flow",
      };
    })
    .sort((a, b) => number(a.rank) - number(b.rank));

  return rows.length ? rows : fallbackRows || [];
}

function buildStockIntervalRows(snapshots, intervalMinutes, threshold) {
  const start = timeToMinutes("09:15");
  const end = timeToMinutes("15:30");
  const byMinute = new Map();

  (snapshots || []).forEach(snapshot => {
    const minute = stockSnapshotMinute(snapshot);
    if (minute) byMinute.set(minute, snapshot);
  });

  return Array.from({ length: Math.ceil((end - start) / intervalMinutes) }, (_, position) => {
    const intervalStart = start + position * intervalMinutes;
    const intervalEnd = Math.min(intervalStart + intervalMinutes, end);
    const records = [];

    for (let minute = intervalStart + 1; minute <= intervalEnd; minute += 1) {
      const record = byMinute.get(minuteLabel(minute));
      if (record) records.push(record);
    }

    const rows = aggregateStockRows(records, [], threshold);
    const sellRows = rows.filter(row => row.side === "SELL");
    const buyRows = rows.filter(row => row.side === "BUY");

    return {
      serial: position + 1,
      label: `${minuteLabel(intervalStart)} - ${minuteLabel(intervalEnd)}`,
      records,
      rows,
      summary: {
        sellAlerts: sellRows.length,
        buyAlerts: buyRows.length,
        totalSellQty: sellRows.reduce((sum, row) => sum + number(row.volumeDelta), 0),
        totalBuyQty: buyRows.reduce((sum, row) => sum + number(row.volumeDelta), 0),
        totalSellValueCrores: Number(sellRows.reduce((sum, row) => sum + number(row.pressureValueCrores), 0).toFixed(2)),
        totalBuyValueCrores: Number(buyRows.reduce((sum, row) => sum + number(row.pressureValueCrores), 0).toFixed(2)),
      },
    };
  });
}

const styles = {
  page: {
    minHeight: "100vh",
    background: colors.bg,
    color: "#f8fafc",
    padding: 18,
    fontFamily: "Consolas, Monaco, 'Courier New', monospace",
  },
  top: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 14,
  },
  title: {
    margin: 0,
    color: colors.gold,
    fontSize: 26,
    letterSpacing: 0,
    textShadow: "0 0 14px rgba(247, 201, 72, 0.45)",
  },
  sub: {
    color: colors.muted,
    fontSize: 12,
    marginTop: 4,
  },
  button: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    border: `1px solid ${colors.line}`,
    background: "#11100b",
    color: colors.gold,
    padding: "9px 12px",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 800,
  },
  select: {
    border: `1px solid ${colors.line}`,
    background: "#11100b",
    color: colors.gold,
    padding: "9px 12px",
    borderRadius: 6,
    fontWeight: 800,
    minWidth: 180,
  },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: 10,
    marginBottom: 14,
  },
  stat: {
    border: `1px solid ${colors.line}`,
    background: colors.panel,
    borderRadius: 6,
    padding: 12,
  },
  statLabel: {
    color: colors.muted,
    fontSize: 11,
    textTransform: "uppercase",
  },
  statValue: {
    marginTop: 5,
    color: colors.gold,
    fontSize: 20,
    fontWeight: 900,
  },
  tableWrap: {
    border: `1px solid ${colors.line}`,
    background: "#050302",
    borderRadius: 6,
    overflow: "auto",
    boxShadow: "0 0 24px rgba(255, 37, 58, 0.08)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 1080,
  },
  th: {
    background: colors.header,
    color: "#03120e",
    fontWeight: 900,
    padding: "8px 10px",
    borderRight: "2px solid rgba(0,0,0,0.35)",
    whiteSpace: "nowrap",
    fontSize: 13,
  },
  td: {
    padding: "7px 10px",
    borderTop: "1px solid rgba(255,255,255,0.14)",
    borderRight: "1px solid rgba(255,255,255,0.08)",
    whiteSpace: "nowrap",
    fontWeight: 900,
    fontSize: 13,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: colors.gold,
    fontSize: 16,
    fontWeight: 900,
    margin: "18px 0 8px",
    flexWrap: "wrap",
  },
  error: {
    color: "#fecaca",
    background: "rgba(127, 29, 29, 0.45)",
    border: "1px solid rgba(248, 113, 113, 0.5)",
    padding: 10,
    borderRadius: 6,
    marginBottom: 12,
  },
  liveTabs: {
    display: "inline-flex",
    gap: 6,
    flexWrap: "wrap",
  },
  liveTab: {
    border: `1px solid ${colors.line}`,
    background: "#080604",
    color: colors.gold,
    padding: "9px 12px",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 900,
  },
  liveTabActive: {
    background: colors.gold,
    color: "#080604",
  },
};

export default function BnSnDhanStocksPage({ desk, title, gridTitle, activeTab }) {
  const [snapshot, setSnapshot] = useState(null);
  const [status, setStatus] = useState(null);
  const [minuteSnapshots, setMinuteSnapshots] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedHistoryDate, setSelectedHistoryDate] = useState("");
  const [liveGridOpen, setLiveGridOpen] = useState(true);
  const [liveGridTab, setLiveGridTab] = useState("live");
  const [expandedStockInterval, setExpandedStockInterval] = useState("");
  const [savedHistoryOpen, setSavedHistoryOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [controlLoading, setControlLoading] = useState(false);
  const [error, setError] = useState("");

  const goToTab = path => {
    window.location.assign(path);
  };

  const loadLive = useCallback(async (refresh = false) => {
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/api/bnsndhanstocksserver/${desk}/live${refresh ? "?refresh=1" : ""}`);
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        throw new Error(data.error || `${title} live fetch failed`);
      }

      setSnapshot(data.snapshot || null);
      setMinuteSnapshots(data.minuteSnapshots || []);
      setStatus(data.status || null);
    } catch (err) {
      setError(err.message || `${title} live fetch failed`);
    } finally {
      setLoading(false);
    }
  }, [desk, title]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/bnsndhanstocksserver/${desk}/history`);
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setHistory(data.records || []);
      }
    } catch {
      setHistory([]);
      setSelectedHistoryDate("");
    }
  }, [desk]);

  const setRunning = useCallback(async (nextRunning) => {
    setControlLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/api/bnsndhanstocksserver/${desk}/${nextRunning ? "start" : "stop"}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        throw new Error(data.error || `${title} ${nextRunning ? "start" : "stop"} failed`);
      }

      setSnapshot(data.snapshot || null);
      setMinuteSnapshots(data.minuteSnapshots || []);
      setStatus(current => ({
        ...(current || {}),
        enabled: data.enabled === true,
      }));
      await loadHistory();
    } catch (err) {
      setError(err.message || `${title} ${nextRunning ? "start" : "stop"} failed`);
    } finally {
      setControlLoading(false);
    }
  }, [desk, loadHistory, title]);

  useEffect(() => {
    loadLive(false);
    loadHistory();

    const liveTimer = setInterval(() => {
      if (status?.enabled) loadLive(false);
    }, 60000);
    const historyTimer = setInterval(loadHistory, 60000);

    return () => {
      clearInterval(liveTimer);
      clearInterval(historyTimer);
    };
  }, [loadHistory, loadLive, status?.enabled]);

  const liveDate = snapshot?.date || "";
  const dateOptions = useMemo(
    () => [...new Set([liveDate, ...history.map(record => record.date)].filter(Boolean))],
    [history, liveDate]
  );
  const selectedHistory = useMemo(() => {
    return history.find(record => record.date === selectedHistoryDate) || null;
  }, [history, selectedHistoryDate]);
  const selectedLiveDate = !selectedHistoryDate || selectedHistoryDate === liveDate;
  const activeStockRecord = selectedLiveDate ? snapshot : selectedHistory;
  const activeMinuteSnapshots = selectedLiveDate
    ? minuteSnapshots
    : selectedHistory?.minuteSnapshots || [];
  const rowThreshold = number(status?.thresholdQuantity || activeStockRecord?.thresholdQuantity || 3000);
  const rows = selectedLiveDate
    ? activeStockRecord?.rows || []
    : aggregateStockRows(activeMinuteSnapshots, activeStockRecord?.rows || [], rowThreshold);
  const useSavedTotalFallback = liveGridTab === "live" && (!selectedLiveDate || activeStockRecord?.marketStatus !== "OPEN");
  const summary = activeStockRecord?.summary || {};
  const stockIntervalRows = useMemo(
    () => buildStockIntervalRows(activeMinuteSnapshots, liveGridTab === "5" ? 5 : 1, rowThreshold),
    [activeMinuteSnapshots, liveGridTab, rowThreshold]
  );
  const latestStockInterval = useMemo(
    () => [...stockIntervalRows].reverse().find(row => row.rows.length),
    [stockIntervalRows]
  );

  useEffect(() => {
    if (liveGridTab === "live" || !latestStockInterval) return;
    setExpandedStockInterval(latestStockInterval.label);
  }, [latestStockInterval, liveGridTab]);

  useEffect(() => {
    if (!dateOptions.length) return;
    if (!selectedHistoryDate || !dateOptions.includes(selectedHistoryDate)) {
      setSelectedHistoryDate(dateOptions[0]);
    }
  }, [dateOptions, selectedHistoryDate]);

  const renderRows = (gridRows, emptyText, keyPrefix = "live", useTotalFallback = false) => (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr>
            {["Wt", "Time", "Name", "Rate", "Qty", "Value(Cr)", "Side", "Delta", "%", "Reason"].map(head => (
              <th key={head} style={styles.th}>{head}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {gridRows.map(row => (
            <tr
              key={`${keyPrefix}-${row.symbol}`}
              className={row.alert ? "stock-blink" : ""}
              style={{
                background: row.side === "BUY"
                  ? "rgba(0, 112, 43, 0.72)"
                  : row.side === "SELL"
                    ? "rgba(71, 5, 15, 0.92)"
                    : "rgba(20, 16, 16, 0.88)",
                color: rowColor(row.side),
              }}
            >
              <td style={styles.td}>{row.rank}. {fmt(row.weight, 2)}%</td>
              <td style={styles.td}>{row.time}</td>
              <td style={styles.td}>{row.symbol}</td>
              <td style={styles.td}>{fmt(row.ltp, 2)}</td>
              <td style={styles.td}>{fmt(displayQty(row, useTotalFallback))}</td>
              <td style={styles.td}>{fmt(displayValueCrores(row, useTotalFallback), 1)}</td>
              <td style={styles.td}>{pressureLabel(row)}</td>
              <td style={styles.td}>{signed(row.priceDelta, 2)}</td>
              <td style={styles.td}>{fmt(row.percentChange, 2)}</td>
              <td style={styles.td}>{row.reason}</td>
            </tr>
          ))}
          {!gridRows.length && (
            <tr>
              <td style={styles.td} colSpan={10}>{emptyText}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div style={styles.page}>
      <style>
        {`
          @keyframes stockBlink {
            0%, 100% { filter: brightness(1); box-shadow: inset 0 0 0 rgba(255,255,255,0); }
            50% { filter: brightness(1.7); box-shadow: inset 0 0 22px rgba(255,255,255,0.20); }
          }
          .stock-blink {
            animation: stockBlink 0.85s infinite;
          }
          @media (max-width: 760px) {
            .stocks-title-block h1 { font-size: 20px !important; }
            .stocks-actions { width: 100%; }
            .stocks-actions button,
            .stocks-actions select { flex: 1; justify-content: center; min-width: 0; }
          }
        `}
      </style>

      <div style={styles.top}>
        <div className="stocks-title-block">
          <h1 style={styles.title}>{title}</h1>
          <div style={styles.sub}>
            Secret Page Dhan credentials | Auto fetch every minute | Auto EOD save | Quantity threshold {fmt(status?.thresholdQuantity || snapshot?.thresholdQuantity || 3000)} | Last {snapshot?.retentionDays || 14} saved days
          </div>
          <div style={{ ...styles.liveTabs, marginTop: 12 }}>
            {[
              { id: "nifty", label: "Dhan Nifty 50", path: "/dhan-stocks" },
              { id: "banknifty", label: "Dhan BankNifty", path: "/bndhanstocks" },
              { id: "sensex", label: "Dhan Sensex", path: "/sndhanstocks" },
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                style={{
                  ...styles.liveTab,
                  ...(activeTab === tab.id ? styles.liveTabActive : {}),
                }}
                onClick={() => goToTab(tab.path)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="stocks-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            style={{
              ...styles.button,
              color: status?.enabled ? colors.red : colors.green,
            }}
            onClick={() => setRunning(!status?.enabled)}
            disabled={controlLoading}
          >
            {controlLoading ? "Please wait" : status?.enabled ? "Stop" : "Start"}
          </button>
          <button style={styles.button} onClick={() => loadLive(true)} disabled={loading}>
            <RefreshCw size={15} /> {loading ? "Fetching" : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.statGrid}>
        <div style={styles.stat}>
          <div style={styles.statLabel}>Last Fetch</div>
          <div style={styles.statValue}>{snapshot?.rows?.[0]?.time || "--"}</div>
        </div>
        <div style={styles.stat}>
          <div style={styles.statLabel}>Active Alerts</div>
          <div style={styles.statValue}>{fmt(summary.activeAlerts)}</div>
        </div>
        <div style={styles.stat}>
          <div style={styles.statLabel}>Sell Qty / Value</div>
          <div style={{ ...styles.statValue, color: colors.red }}>
            {fmt(summary.totalSellQty)} / {fmt(summary.totalSellValueCrores, 1)} Cr
          </div>
        </div>
        <div style={styles.stat}>
          <div style={styles.statLabel}>Buy Qty / Value</div>
          <div style={{ ...styles.statValue, color: colors.green }}>
            {fmt(summary.totalBuyQty)} / {fmt(summary.totalBuyValueCrores, 1)} Cr
          </div>
        </div>
      </div>

      <div style={styles.sectionTitle}>
        <button
          type="button"
          style={{ ...styles.button, padding: "9px 12px", minWidth: 0 }}
          onClick={() => setLiveGridOpen(open => !open)}
        >
          <Wifi size={17} /> {gridTitle}
          <ChevronDown
            size={16}
            style={{
              transform: liveGridOpen ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.16s ease",
            }}
          />
        </button>
        {liveGridOpen && (
          <div style={styles.liveTabs}>
            {[
              { id: "live", label: "Live" },
              { id: "1", label: "1 Min" },
              { id: "5", label: "5 Min" },
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                style={{
                  ...styles.liveTab,
                  ...(liveGridTab === tab.id ? styles.liveTabActive : {}),
                }}
                onClick={() => setLiveGridTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
            <select
              style={{ ...styles.select, minWidth: 190 }}
              value={selectedHistoryDate}
              onChange={event => setSelectedHistoryDate(event.target.value)}
            >
              {dateOptions.map(date => (
                <option key={date} value={date}>
                  {date}
                </option>
              ))}
              {!dateOptions.length && <option value="">No saved dates</option>}
            </select>
          </div>
        )}
      </div>

      {liveGridOpen && liveGridTab === "live" && renderRows(
        rows,
        `No live ${gridTitle} data loaded yet.`,
        selectedHistoryDate || "live",
        useSavedTotalFallback
      )}

      {liveGridOpen && liveGridTab !== "live" && (
        <div style={styles.tableWrap}>
          <table style={{ ...styles.table, minWidth: 1160 }}>
            <thead>
              <tr>
                {["#", "Interval", "Rows", "Sell Alerts", "Buy Alerts", "Sell Qty", "Buy Qty", "Sell Cr", "Buy Cr"].map(head => (
                  <th key={head} style={styles.th}>{head}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stockIntervalRows.map(row => {
                const isExpanded = expandedStockInterval === row.label;
                return (
                  <React.Fragment key={row.label}>
                    <tr
                      onClick={() => row.rows.length && setExpandedStockInterval(current => current === row.label ? "" : row.label)}
                      style={{
                        background: row.rows.length ? "rgba(20, 16, 16, 0.88)" : "rgba(255,255,255,0.015)",
                        color: row.rows.length ? colors.gold : colors.muted,
                        cursor: row.rows.length ? "pointer" : "default",
                      }}
                    >
                      <td style={styles.td}>{row.serial}</td>
                      <td style={styles.td}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {row.rows.length && (
                            <ChevronDown
                              size={14}
                              style={{
                                transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                                transition: "transform 0.16s ease",
                              }}
                            />
                          )}
                          {row.label}
                        </span>
                      </td>
                      <td style={styles.td}>{row.rows.length ? row.rows.length : "--"}</td>
                      <td style={{ ...styles.td, color: colors.red }}>{row.rows.length ? fmt(row.summary.sellAlerts) : "--"}</td>
                      <td style={{ ...styles.td, color: colors.green }}>{row.rows.length ? fmt(row.summary.buyAlerts) : "--"}</td>
                      <td style={{ ...styles.td, color: colors.red }}>{row.rows.length ? fmt(row.summary.totalSellQty) : "--"}</td>
                      <td style={{ ...styles.td, color: colors.green }}>{row.rows.length ? fmt(row.summary.totalBuyQty) : "--"}</td>
                      <td style={{ ...styles.td, color: colors.red }}>{row.rows.length ? fmt(row.summary.totalSellValueCrores, 1) : "--"}</td>
                      <td style={{ ...styles.td, color: colors.green }}>{row.rows.length ? fmt(row.summary.totalBuyValueCrores, 1) : "--"}</td>
                    </tr>

                    {isExpanded && row.rows.length > 0 && (
                      <tr>
                        <td colSpan={9} style={{ padding: 0, borderTop: `1px solid ${colors.line}` }}>
                          {renderRows(row.rows, "No interval rows available.", row.label)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={styles.sectionTitle}>
        <button
          type="button"
          style={{ ...styles.button, padding: "9px 12px", minWidth: 0 }}
          onClick={() => setSavedHistoryOpen(open => !open)}
        >
          <Archive size={17} /> Saved EOD History - Last 14 Days
          <ChevronDown
            size={16}
            style={{
              transform: savedHistoryOpen ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.16s ease",
            }}
          />
        </button>
        {savedHistoryOpen && (
          <select
            style={styles.select}
            value={selectedHistoryDate}
            onChange={event => setSelectedHistoryDate(event.target.value)}
          >
            {dateOptions.map(date => (
              <option key={date} value={date}>
                {date}
              </option>
            ))}
            {!dateOptions.length && <option value="">No saved dates</option>}
          </select>
        )}
      </div>

      {savedHistoryOpen && (
        <div style={styles.tableWrap}>
          <table style={{ ...styles.table, minWidth: 760 }}>
            <thead>
              <tr>
                {["Date", "Archived", "Sell Qty", "Buy Qty", "Sell Cr", "Buy Cr"].map(head => (
                  <th key={head} style={styles.th}>{head}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {history.map(record => (
                <tr key={record.id} style={{ background: "rgba(20, 16, 16, 0.88)", color: "#f8fafc" }}>
                  <td style={styles.td}>{record.date}</td>
                  <td style={styles.td}>{record.archivedAt ? new Date(record.archivedAt).toLocaleString("en-IN") : "--"}</td>
                  <td style={{ ...styles.td, color: colors.red }}>{fmt(record.summary?.totalSellQty)}</td>
                  <td style={{ ...styles.td, color: colors.green }}>{fmt(record.summary?.totalBuyQty)}</td>
                  <td style={{ ...styles.td, color: colors.red }}>{fmt(record.summary?.totalSellValueCrores, 1)}</td>
                  <td style={{ ...styles.td, color: colors.green }}>{fmt(record.summary?.totalBuyValueCrores, 1)}</td>
                </tr>
              ))}
              {!history.length && (
                <tr>
                  <td style={styles.td} colSpan={6}>No EOD Dhan stock records saved yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
